import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { ChevronLeftIcon, ChevronRightIcon, CalendarIcon, SearchIcon, CheckIcon, FilterIcon, RefreshCwIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CalendarEventCard } from '../components/ui/CalendarEventCard';
import { ModalDialog } from '../components/ui/ModalDialog';
import { StatusBadge } from '../components/ui/StatusBadge';
import { useIsMobile, useIsTablet } from '../hooks/useMediaQuery';
import { useRole } from '../hooks/useRole';
// --- Types ---
type ViewMode = 'day' | 'week' | 'month' | 'year';
interface CalendarEvent {
  id: string;
  title: string;
  patientName: string;
  doctor: string;
  machine: string;
  examType: string;
  date: Date;
  startHour: number; // e.g., 8.5 for 08:30
  duration: number; // in hours
  colorClass: string;
}
// --- Constants & Mock Data ---
const DOCTORS = ['Dr. Salvatore Verdi', 'Dr.ssa Laura Bianchi', 'Dr. Marco Gialli', 'Dr.ssa Anna Neri', 'Dr. Giuseppe Romano', 'Dr.ssa Elena Colombo'];
const EXAM_TYPES = ['RM Encefalo', 'RM Colonna', 'TAC Torace', 'TAC Addome', 'Ecografia Addome', 'Ecografia Pelvi', 'Radiografia Torace', 'Radiografia Colonna', 'Mammografia'];
const MACHINES = ['Siemens Magnetom Aera', 'GE Revolution CT', 'Philips Affiniti 70', 'Carestream DRX-Evolution', 'Hologic Selenia Dimensions', 'Hitachi Oasis 1.2T'];
const MONTHS_IT = ['Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno', 'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'];
const DAYS_IT = ['Domenica', 'Lunedì', 'Martedì', 'Mercoledì', 'Giovedì', 'Venerdì', 'Sabato'];
const DAYS_SHORT_IT = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
// Base date for mock data: March 11, 2026 (Wednesday)
const BASE_DATE = new Date(2026, 2, 11);
const getExamColor = (exam: string) => {
  if (exam.startsWith('RM')) return 'border-medical-green';
  if (exam.startsWith('TAC')) return 'border-medical-orange';
  if (exam.startsWith('Ecografia')) return 'border-status-info';
  if (exam.startsWith('Radiografia')) return 'border-purple-500';
  if (exam.startsWith('Mammografia')) return 'border-pink-500';
  return 'border-clinical-gray-400';
};
const generateMockEvents = (): CalendarEvent[] => {
  const events: CalendarEvent[] = [];
  let idCounter = 1;
  const addEvent = (dayOffset: number, startHour: number, duration: number, examType: string, patientName: string, doctor: string, machine: string) => {
    const date = new Date(BASE_DATE);
    date.setDate(date.getDate() + dayOffset);
    events.push({
      id: `evt-${idCounter++}`,
      title: examType,
      patientName,
      doctor,
      machine,
      examType,
      date,
      startHour,
      duration,
      colorClass: getExamColor(examType)
    });
  };
  // Monday (Mar 9)
  addEvent(-2, 8, 1, 'RM Encefalo', 'Marco Rossi', DOCTORS[0], MACHINES[0]);
  addEvent(-2, 9.5, 0.5, 'TAC Torace', 'Giulia Bianchi', DOCTORS[3], MACHINES[1]);
  addEvent(-2, 10.5, 0.5, 'Ecografia Addome', 'Paola Gentile', DOCTORS[0], MACHINES[2]);
  addEvent(-2, 11, 1, 'Ecografia Addome', 'Luca Romano', DOCTORS[2], MACHINES[2]);
  addEvent(-2, 14, 0.5, 'Radiografia Torace', 'Alberto Mancini', DOCTORS[0], MACHINES[3]);
  // Tuesday (Mar 10)
  addEvent(-1, 8, 1, 'RM Colonna', 'Alessandro Ricci', DOCTORS[0], MACHINES[0]);
  addEvent(-1, 8.5, 0.5, 'Radiografia Torace', 'Elena Colombo', DOCTORS[4], MACHINES[3]);
  addEvent(-1, 9.5, 0.5, 'Ecografia Pelvi', 'Lucia Ferri', DOCTORS[0], MACHINES[2]);
  addEvent(-1, 11, 1, 'TAC Addome', 'Francesca Moretti', DOCTORS[0], MACHINES[1]);
  addEvent(-1, 14, 0.5, 'Mammografia', 'Sofia Marino', DOCTORS[1], MACHINES[4]);
  addEvent(-1, 15, 1, 'TAC Addome', 'Matteo Costa', DOCTORS[3], MACHINES[1]);
  addEvent(-1, 15.5, 0.5, 'Radiografia Colonna', 'Stefano Pellegrini', DOCTORS[0], MACHINES[3]);
  // Wednesday (Mar 11 - TODAY)
  addEvent(0, 8, 1, 'RM Encefalo', 'Francesca Neri', DOCTORS[0], MACHINES[0]);
  addEvent(0, 9.5, 0.5, 'Ecografia Pelvi', 'Antonio Gialli', DOCTORS[2], MACHINES[2]);
  addEvent(0, 9.5, 0.5, 'Ecografia Addome', 'Roberto Marchetti', DOCTORS[0], MACHINES[2]);
  addEvent(0, 10.5, 0.5, 'Radiografia Colonna', 'Valentina Russo', DOCTORS[4], MACHINES[3]);
  addEvent(0, 11, 0.5, 'TAC Torace', 'Simona Vitale', DOCTORS[0], MACHINES[1]);
  addEvent(0, 11.5, 1, 'TAC Torace', 'Roberto Ferrari', DOCTORS[3], MACHINES[1]);
  addEvent(0, 14, 1.5, 'RM Colonna', 'Chiara Esposito', DOCTORS[0], MACHINES[0]);
  addEvent(0, 15.5, 0.5, 'TAC Addome', 'Giuliana Parisi', DOCTORS[0], MACHINES[1]);
  addEvent(0, 16, 0.5, 'Mammografia', 'Silvia Romano', DOCTORS[1], MACHINES[4]);
  // Thursday (Mar 12)
  addEvent(1, 8.5, 1, 'RM Encefalo', 'Valentina Greco', DOCTORS[0], MACHINES[0]);
  addEvent(1, 9, 1, 'TAC Addome', 'Giorgio Conti', DOCTORS[3], MACHINES[1]);
  addEvent(1, 10, 0.5, 'Ecografia Addome', 'Martina Bruno', DOCTORS[2], MACHINES[2]);
  addEvent(1, 10, 0.5, 'Ecografia Pelvi', 'Marta Rinaldi', DOCTORS[0], MACHINES[2]);
  addEvent(1, 11.5, 1, 'TAC Torace', 'Fabio Caruso', DOCTORS[0], MACHINES[1]);
  addEvent(1, 14, 0.5, 'Radiografia Colonna', 'Davide Fontana', DOCTORS[0], MACHINES[3]);
  addEvent(1, 15, 0.5, 'Radiografia Torace', 'Sara Gallo', DOCTORS[4], MACHINES[3]);
  // Friday (Mar 13)
  addEvent(2, 8, 1, 'RM Colonna', 'Luca De Luca', DOCTORS[0], MACHINES[0]);
  addEvent(2, 8.5, 1.5, 'RM Colonna', 'Andrea Fontana', DOCTORS[5], MACHINES[0]);
  addEvent(2, 10, 0.5, 'Ecografia Addome', 'Alessia Fabbri', DOCTORS[0], MACHINES[2]);
  addEvent(2, 11, 0.5, 'Mammografia', 'Laura Santoro', DOCTORS[1], MACHINES[4]);
  addEvent(2, 11, 0.5, 'Radiografia Colonna', 'Teresa Moretti', DOCTORS[0], MACHINES[3]);
  addEvent(2, 14, 1, 'TAC Addome', 'Nicola Barbieri', DOCTORS[0], MACHINES[1]);
  addEvent(2, 14.5, 1, 'TAC Torace', 'Simone Leone', DOCTORS[3], MACHINES[1]);
  // Next Week
  addEvent(5, 8.5, 1, 'RM Encefalo', 'Paolo Longo', DOCTORS[0], MACHINES[0]);
  addEvent(5, 10, 0.5, 'Ecografia Addome', 'Giorgio Conti', DOCTORS[0], MACHINES[2]);
  addEvent(5, 14, 1, 'TAC Torace', 'Matteo Gatti', DOCTORS[0], MACHINES[1]);
  addEvent(6, 9, 1, 'RM Colonna', 'Chiara Rizzo', DOCTORS[0], MACHINES[0]);
  addEvent(6, 11, 0.5, 'Radiografia Torace', 'Sara Lombardi', DOCTORS[0], MACHINES[3]);
  addEvent(7, 8, 0.5, 'Ecografia Addome', 'Giuseppe Sala', DOCTORS[2], MACHINES[2]);
  addEvent(7, 9, 1, 'RM Encefalo', 'Fabio Caruso', DOCTORS[0], MACHINES[0]);
  addEvent(7, 14, 0.5, 'Ecografia Pelvi', 'Marta Rinaldi', DOCTORS[0], MACHINES[2]);
  return events;
};
const MOCK_EVENTS = generateMockEvents();
// --- Date Utilities ---
const isSameDay = (d1: Date, d2: Date) => d1.getFullYear() === d2.getFullYear() && d1.getMonth() === d2.getMonth() && d1.getDate() === d2.getDate();
const getStartOfWeek = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
};
const addDays = (date: Date, days: number) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
const formatTime = (hour: number) => {
  const h = Math.floor(hour);
  const m = Math.round((hour - h) * 60);
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
};
// --- Components ---
// Filter Dropdown Component
interface MultiSelectFilterProps {
  label: string;
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
}
function MultiSelectFilter({
  label,
  options,
  selected,
  onChange,
  isOpen,
  onToggle,
  onClose
}: MultiSelectFilterProps) {
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);
  const filteredOptions = options.filter(opt => opt.toLowerCase().includes(search.toLowerCase()));
  const toggleOption = (opt: string) => {
    if (selected.includes(opt)) {
      onChange(selected.filter(item => item !== opt));
    } else {
      onChange([...selected, opt]);
    }
  };
  const isAllSelected = selected.length === options.length || selected.length === 0;
  return <div className="relative" ref={ref} data-id="element-492">
      <button onClick={onToggle} className={`flex items-center justify-between w-full sm:w-48 px-3 py-2 text-sm bg-white border rounded-medical transition-colors ${isOpen || selected.length > 0 ? 'border-medical-green text-medical-green' : 'border-clinical-gray-200 text-text-secondary hover:border-clinical-gray-300'}`} data-id="element-493">
        <span className="truncate pr-2" data-id="element-494">
          {selected.length > 0 ? `${label} (${selected.length})` : `Tutti i ${label.toLowerCase()}`}
        </span>
        <FilterIcon size={14} className="shrink-0" data-id="element-495" />
      </button>

      <AnimatePresence data-id="element-496">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: 4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 4
      }} transition={{
        duration: 0.15
      }} className="absolute z-50 top-full left-0 mt-1 w-64 bg-white border border-clinical-gray-200 rounded-medical-lg shadow-modal overflow-hidden" data-id="element-497">
            <div className="p-2 border-b border-clinical-gray-100" data-id="element-498">
              <div className="relative" data-id="element-499">
                <SearchIcon size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-tertiary" data-id="element-500" />
                <input type="text" placeholder="Cerca..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-8 pr-3 py-1.5 text-sm bg-clinical-gray-50 border border-clinical-gray-200 rounded-md focus:outline-none focus:border-medical-green focus:ring-1 focus:ring-medical-green transition-all" data-id="element-501" />
              </div>
            </div>
            <div className="max-h-60 overflow-y-auto p-1" data-id="element-502">
              <button onClick={() => onChange([])} className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-clinical-gray-50 rounded-md flex items-center justify-between" data-id="element-503">
                <span data-id="element-504">Tutti</span>
                {isAllSelected && <CheckIcon size={14} className="text-medical-green" data-id="element-505" />}
              </button>
              {filteredOptions.map(opt => <button key={opt} onClick={() => toggleOption(opt)} className="w-full text-left px-3 py-2 text-sm text-text-primary hover:bg-clinical-gray-50 rounded-md flex items-center justify-between" data-id="element-506">
                  <span className="truncate pr-2" data-id="element-507">{opt}</span>
                  {selected.includes(opt) && <CheckIcon size={14} className="text-medical-green shrink-0" data-id="element-508" />}
                </button>)}
              {filteredOptions.length === 0 && <div className="px-3 py-4 text-center text-sm text-text-tertiary" data-id="element-509">
                  Nessun risultato
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
// --- Main Page Component ---
export function Calendar() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const {
    role
  } = useRole();
  const CURRENT_USER = role === 'medico' ? 'Dr. Salvatore Verdi' : 'Superadmin';
  // State
  const [currentDate, setCurrentDate] = useState(BASE_DATE);
  const [view, setView] = useState<ViewMode>(isTablet ? 'day' : 'week');
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null);
  // Filters State
  const [selectedDoctors, setSelectedDoctors] = useState<string[]>([]);
  const [selectedExams, setSelectedExams] = useState<string[]>([]);
  const [selectedMachines, setSelectedMachines] = useState<string[]>([]);
  // Force day view on tablet
  useEffect(() => {
    if (isTablet && view !== 'day') {
      setView('day');
    }
  }, [isTablet]);
  const dashboardEvents = useMemo(() => {
    if (role === 'medico') {
      return MOCK_EVENTS.filter(e => e.doctor === CURRENT_USER);
    }
    return MOCK_EVENTS;
  }, [role, CURRENT_USER]);
  // Derived Data
  const filteredEvents = useMemo(() => {
    return dashboardEvents.filter(event => {
      const matchDoctor = selectedDoctors.length === 0 || selectedDoctors.includes(event.doctor);
      const matchExam = selectedExams.length === 0 || selectedExams.includes(event.examType);
      const matchMachine = selectedMachines.length === 0 || selectedMachines.includes(event.machine);
      return matchDoctor && matchExam && matchMachine;
    });
  }, [dashboardEvents, selectedDoctors, selectedExams, selectedMachines]);
  // Navigation Handlers
  const handlePrev = () => {
    const newDate = new Date(currentDate);
    if (view === 'day') newDate.setDate(newDate.getDate() - 1);
    if (view === 'week') newDate.setDate(newDate.getDate() - 7);
    if (view === 'month') newDate.setMonth(newDate.getMonth() - 1);
    if (view === 'year') newDate.setFullYear(newDate.getFullYear() - 1);
    setCurrentDate(newDate);
  };
  const handleNext = () => {
    const newDate = new Date(currentDate);
    if (view === 'day') newDate.setDate(newDate.getDate() + 1);
    if (view === 'week') newDate.setDate(newDate.getDate() + 7);
    if (view === 'month') newDate.setMonth(newDate.getMonth() + 1);
    if (view === 'year') newDate.setFullYear(newDate.getFullYear() + 1);
    setCurrentDate(newDate);
  };
  const handleToday = () => {
    setCurrentDate(BASE_DATE);
  };
  const formatPeriodTitle = () => {
    const month = MONTHS_IT[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    if (view === 'day') {
      const dayName = DAYS_IT[currentDate.getDay()];
      return `${dayName} ${currentDate.getDate()} ${month} ${year}`;
    }
    if (view === 'week') {
      const start = getStartOfWeek(currentDate);
      const end = addDays(start, 6);
      if (start.getMonth() === end.getMonth()) {
        return `${start.getDate()} - ${end.getDate()} ${month} ${year}`;
      }
      return `${start.getDate()} ${MONTHS_IT[start.getMonth()]} - ${end.getDate()} ${MONTHS_IT[end.getMonth()]} ${year}`;
    }
    if (view === 'month') {
      return `${month} ${year}`;
    }
    return `${year}`;
  };
  // --- Render Views ---
  const renderDayView = () => {
    const hours = Array.from({
      length: 13
    }, (_, i) => i + 7); // 07:00 to 19:00
    const dayEvents = filteredEvents.filter(e => isSameDay(e.date, currentDate));
    return <div className="flex-1 bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 overflow-hidden flex flex-col min-h-[500px]" data-id="element-510">
        <div className="grid grid-cols-[60px_1fr] md:grid-cols-[80px_1fr] border-b border-clinical-gray-200 bg-clinical-gray-50 shrink-0" data-id="element-511">
          <div className="p-3 md:p-4 border-r border-clinical-gray-200 text-center text-xs md:text-sm font-medium text-text-secondary" data-id="element-512">
            Ora
          </div>
          <div className="p-3 md:p-4 text-center text-sm font-medium text-text-primary" data-id="element-513">
            {DAYS_IT[currentDate.getDay()]}, {currentDate.getDate()}{' '}
            {MONTHS_IT[currentDate.getMonth()]}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto relative" data-id="element-514">
          <div className="grid grid-cols-[60px_1fr] md:grid-cols-[80px_1fr] min-h-[780px]" data-id="element-515">
            <div className="border-r border-clinical-gray-200" data-id="element-516">
              {hours.map(hour => <div key={hour} className="h-[60px] border-b border-clinical-gray-100 p-2 text-right text-[10px] md:text-xs text-text-tertiary" data-id="element-517">
                  {hour.toString().padStart(2, '0')}:00
                </div>)}
            </div>
            <div className="relative" data-id="element-518">
              {hours.map(hour => <div key={hour} className="h-[60px] border-b border-clinical-gray-100 w-full" data-id="element-519" />)}
              {dayEvents.map(event => {
              const top = (event.startHour - 7) * 60;
              const height = event.duration * 60;
              return <div key={event.id} className="absolute left-1 right-2 md:left-2 md:right-4 z-10" style={{
                top: `${top}px`,
                minHeight: `${height - 2}px`
              }} data-id="element-520">
                    <CalendarEventCard patientName={event.patientName} examType={event.examType} doctor={event.doctor} time={`${formatTime(event.startHour)} - ${formatTime(event.startHour + event.duration)}`} colorClass={event.colorClass} onClick={() => setSelectedEvent(event)} data-id="element-521" />
                  </div>;
            })}
              {isSameDay(currentDate, BASE_DATE) && <div className="absolute left-0 right-0 border-t-2 border-status-error z-20 pointer-events-none" style={{
              top: `${(10.5 - 7) * 60}px`
            }} data-id="element-522">
                  <div className="absolute -left-1.5 md:-left-2 -top-1.5 w-3 h-3 rounded-full bg-status-error" data-id="element-523" />
                </div>}
            </div>
          </div>
        </div>
      </div>;
  };
  const renderWeekView = () => {
    const hours = Array.from({
      length: 13
    }, (_, i) => i + 7);
    const startOfWeek = getStartOfWeek(currentDate);
    const weekDays = Array.from({
      length: 7
    }, (_, i) => addDays(startOfWeek, i));
    return <div className="flex-1 bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 overflow-hidden flex flex-col min-h-[500px]" data-id="element-524">
        <div className="grid grid-cols-[50px_repeat(7,1fr)] md:grid-cols-[60px_repeat(7,1fr)] border-b border-clinical-gray-200 bg-clinical-gray-50 shrink-0" data-id="element-525">
          <div className="p-2 border-r border-clinical-gray-200 text-center text-[10px] md:text-xs font-medium text-text-secondary flex items-end justify-center pb-3" data-id="element-526">
            GMT+1
          </div>
          {weekDays.map((day, i) => {
          const isToday = isSameDay(day, BASE_DATE);
          return <div key={i} className="p-2 md:p-3 text-center border-r border-clinical-gray-200 last:border-r-0" data-id="element-527">
                <div className={`text-[10px] md:text-xs font-medium uppercase mb-1 ${isToday ? 'text-medical-green' : 'text-text-secondary'}`} data-id="element-528">
                  {DAYS_SHORT_IT[day.getDay()]}
                </div>
                <div className={`text-lg md:text-xl font-medium w-7 h-7 md:w-8 md:h-8 mx-auto flex items-center justify-center rounded-full ${isToday ? 'bg-medical-green text-white' : 'text-text-primary'}`} data-id="element-529">
                  {day.getDate()}
                </div>
              </div>;
        })}
        </div>
        <div className="flex-1 overflow-y-auto relative" data-id="element-530">
          <div className="grid grid-cols-[50px_repeat(7,1fr)] md:grid-cols-[60px_repeat(7,1fr)] min-h-[780px]" data-id="element-531">
            <div className="border-r border-clinical-gray-200" data-id="element-532">
              {hours.map(hour => <div key={hour} className="h-[60px] border-b border-clinical-gray-100 p-1 md:p-2 text-right text-[10px] text-text-tertiary" data-id="element-533">
                  {hour}:00
                </div>)}
            </div>
            {weekDays.map((day, dayIdx) => {
            const dayEvents = filteredEvents.filter(e => isSameDay(e.date, day));
            return <div key={dayIdx} className="relative border-r border-clinical-gray-200 last:border-r-0" data-id="element-534">
                  {hours.map(hour => <div key={hour} className="h-[60px] border-b border-clinical-gray-100 w-full" data-id="element-535" />)}
                  {dayEvents.map(event => {
                const top = (event.startHour - 7) * 60;
                const height = event.duration * 60;
                return <div key={event.id} className="absolute left-0.5 right-0.5 md:left-1 md:right-1 z-10" style={{
                  top: `${top}px`,
                  minHeight: `${height - 2}px`
                }} data-id="element-536">
                        <CalendarEventCard patientName={event.patientName} examType={event.examType} doctor={event.doctor} time={`${formatTime(event.startHour)}`} colorClass={event.colorClass} className="!p-1.5 md:!p-2" onClick={() => setSelectedEvent(event)} data-id="element-537" />
                      </div>;
              })}
                  {isSameDay(day, BASE_DATE) && <div className="absolute left-0 right-0 border-t-2 border-status-error z-20 pointer-events-none" style={{
                top: `${(10.5 - 7) * 60}px`
              }} data-id="element-538" />}
                </div>;
          })}
          </div>
        </div>
      </div>;
  };
  const renderMonthView = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
    const totalDays = lastDay.getDate();
    const calendarDays = [];
    // Previous month padding
    for (let i = startOffset - 1; i >= 0; i--) {
      calendarDays.push({
        date: new Date(year, month, -i),
        isCurrentMonth: false
      });
    }
    // Current month
    for (let i = 1; i <= totalDays; i++) {
      calendarDays.push({
        date: new Date(year, month, i),
        isCurrentMonth: true
      });
    }
    // Next month padding
    const remainingDays = 42 - calendarDays.length; // 6 rows of 7
    for (let i = 1; i <= remainingDays; i++) {
      calendarDays.push({
        date: new Date(year, month + 1, i),
        isCurrentMonth: false
      });
    }
    return <div className="flex-1 bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 overflow-hidden flex flex-col" data-id="element-539">
        <div className="grid grid-cols-7 border-b border-clinical-gray-200 bg-clinical-gray-50 shrink-0" data-id="element-540">
          {DAYS_SHORT_IT.map((day, i) => {
          // Reorder to start with Monday
          const displayDay = i === 6 ? DAYS_SHORT_IT[0] : DAYS_SHORT_IT[i + 1];
          return <div key={i} className="p-2 text-center text-xs font-medium text-text-secondary uppercase" data-id="element-541">
                {displayDay}
              </div>;
        })}
        </div>
        <div className="flex-1 grid grid-cols-7 grid-rows-6" data-id="element-542">
          {calendarDays.map((dayObj, i) => {
          const dayEvents = filteredEvents.filter(e => isSameDay(e.date, dayObj.date)).sort((a, b) => a.startHour - b.startHour);
          const isToday = isSameDay(dayObj.date, BASE_DATE);
          return <div key={i} className={`border-b border-r border-clinical-gray-100 p-1 md:p-2 min-h-[80px] flex flex-col ${!dayObj.isCurrentMonth ? 'bg-clinical-gray-50/50' : ''}`} data-id="element-543">
                <div className="flex justify-between items-start mb-1" data-id="element-544">
                  <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full ${isToday ? 'bg-medical-green text-white' : dayObj.isCurrentMonth ? 'text-text-primary' : 'text-text-tertiary'}`} data-id="element-545">
                    {dayObj.date.getDate()}
                  </span>
                </div>
                <div className="flex-1 overflow-hidden space-y-1" data-id="element-546">
                  {dayEvents.slice(0, 3).map(event => <div key={event.id} onClick={() => setSelectedEvent(event)} className={`text-[10px] truncate px-1.5 py-0.5 rounded border-l-2 ${event.colorClass} bg-white shadow-sm border border-clinical-gray-100 cursor-pointer hover:shadow-md transition-shadow`} data-id="element-547">
                      {formatTime(event.startHour)} {event.patientName}
                    </div>)}
                  {dayEvents.length > 3 && <div className="text-[10px] text-text-tertiary font-medium pl-1" data-id="element-548">
                      +{dayEvents.length - 3} altri
                    </div>}
                </div>
              </div>;
        })}
        </div>
      </div>;
  };
  const renderYearView = () => {
    const year = currentDate.getFullYear();
    const months = Array.from({
      length: 12
    }, (_, i) => i);
    return <div className="flex-1 overflow-y-auto bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-4 md:p-6" data-id="element-549">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-id="element-550">
          {months.map(month => {
          const firstDay = new Date(year, month, 1);
          const lastDay = new Date(year, month + 1, 0);
          const startOffset = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
          const totalDays = lastDay.getDate();
          const days = [];
          for (let i = 0; i < startOffset; i++) days.push(null);
          for (let i = 1; i <= totalDays; i++) days.push(new Date(year, month, i));
          return <div key={month} className="border border-clinical-gray-200 rounded-medical p-3 hover:border-medical-green/50 transition-colors cursor-pointer" onClick={() => {
            setCurrentDate(new Date(year, month, 1));
            setView('month');
          }} data-id="element-551">
                <h3 className="text-sm font-semibold text-text-primary mb-3 pl-1" data-id="element-552">
                  {MONTHS_IT[month]}
                </h3>
                <div className="grid grid-cols-7 gap-1 text-center mb-1" data-id="element-553">
                  {DAYS_SHORT_IT.map((d, i) => {
                const displayDay = i === 6 ? DAYS_SHORT_IT[0] : DAYS_SHORT_IT[i + 1];
                return <div key={i} className="text-[9px] text-text-tertiary font-medium" data-id="element-554">
                        {displayDay.charAt(0)}
                      </div>;
              })}
                </div>
                <div className="grid grid-cols-7 gap-1" data-id="element-555">
                  {days.map((date, i) => {
                if (!date) return <div key={`empty-${i}`} className="aspect-square" data-id="element-556" />;
                const hasEvents = filteredEvents.some(e => isSameDay(e.date, date));
                const isToday = isSameDay(date, BASE_DATE);
                return <div key={i} className="aspect-square flex flex-col items-center justify-center relative" data-id="element-557">
                        <span className={`text-xs flex items-center justify-center w-6 h-6 rounded-full ${isToday ? 'bg-medical-green text-white font-medium' : 'text-text-secondary hover:bg-clinical-gray-100'}`} data-id="element-558">
                          {date.getDate()}
                        </span>
                        {hasEvents && !isToday && <div className="absolute bottom-0 w-1 h-1 rounded-full bg-medical-orange" data-id="element-559" />}
                      </div>;
              })}
                </div>
              </div>;
        })}
        </div>
      </div>;
  };
  const renderMobileAgenda = () => {
    // Show events from current date onwards for the next 7 days
    const agendaDays = Array.from({
      length: 7
    }, (_, i) => addDays(currentDate, i));
    return <div className="flex-1 space-y-6 overflow-y-auto pb-6" data-id="element-560">
        {agendaDays.map((day, i) => {
        const dayEvents = filteredEvents.filter(e => isSameDay(e.date, day)).sort((a, b) => a.startHour - b.startHour);
        if (dayEvents.length === 0) return null;
        const isToday = isSameDay(day, BASE_DATE);
        return <div key={i} className="space-y-3" data-id="element-561">
              <h3 className={`text-sm font-semibold uppercase tracking-wider sticky top-0 bg-clinical-gray-50/90 backdrop-blur py-2 z-10 ${isToday ? 'text-medical-green' : 'text-text-secondary'}`} data-id="element-562">
                {DAYS_IT[day.getDay()]}, {day.getDate()}{' '}
                {MONTHS_IT[day.getMonth()]}
              </h3>
              <div className="space-y-2" data-id="element-563">
                {dayEvents.map(event => <div key={event.id} data-id="element-564">
                    <CalendarEventCard patientName={event.patientName} examType={event.examType} doctor={event.doctor} time={`${formatTime(event.startHour)} - ${formatTime(event.startHour + event.duration)}`} colorClass={event.colorClass} onClick={() => setSelectedEvent(event)} data-id="element-565" />
                  </div>)}
              </div>
            </div>;
      })}
        {filteredEvents.length === 0 && <div className="text-center py-12 text-text-secondary text-sm" data-id="element-566">
            Nessun evento trovato per i filtri selezionati.
          </div>}
      </div>;
  };
  return <div className="space-y-4 md:space-y-6 h-full flex flex-col min-h-0" data-id="element-567">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4 shrink-0" data-id="element-568">
        <div data-id="element-569">
          <div className="flex items-center gap-3 mb-1 md:mb-2" data-id="element-570">
            <h1 className="text-2xl md:text-page-title text-text-primary font-bold" data-id="element-571">
              Calendario
            </h1>
          </div>
          <p className="text-sm md:text-body text-text-secondary" data-id="element-572">
            Pianificazione diagnostica e allocazione risorse operative.
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0 self-start lg:self-center" data-id="element-573">
          <span className="text-xs text-text-tertiary" data-id="element-574">
            Ultimo import: 11/03/2026 07:45
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-status-success-bg text-status-success" data-id="element-575">
            Completato
          </span>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 md:gap-4" data-id="element-576">
        <div className="flex items-center justify-between sm:justify-start gap-1 bg-white rounded-medical border border-clinical-gray-200 p-1 shadow-sm relative" data-id="element-577">
          <button onClick={handleToday} className="px-3 py-1.5 text-sm font-medium text-text-primary hover:bg-clinical-gray-50 rounded transition-colors" data-id="element-578">
            Oggi
          </button>
          <div className="w-px h-4 bg-clinical-gray-200 mx-1" data-id="element-579" />
          <button onClick={handlePrev} className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-clinical-gray-50 rounded transition-colors" data-id="element-580">
            <ChevronLeftIcon size={18} data-id="element-581" />
          </button>
          <button onClick={handleNext} className="p-1.5 text-text-secondary hover:text-text-primary hover:bg-clinical-gray-50 rounded transition-colors" data-id="element-582">
            <ChevronRightIcon size={18} data-id="element-583" />
          </button>
          <button onClick={() => setShowDatePicker(!showDatePicker)} className="text-sm font-medium text-text-primary px-3 py-1.5 min-w-[140px] text-center hover:bg-clinical-gray-50 rounded transition-colors" data-id="element-584">
            {formatPeriodTitle()}
          </button>

          <AnimatePresence data-id="element-585">
            {showDatePicker && <motion.div initial={{
            opacity: 0,
            y: 4
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: 4
          }} transition={{
            duration: 0.15
          }} className="absolute z-50 top-full left-0 mt-2 bg-white border border-clinical-gray-200 rounded-medical-lg shadow-modal p-4 w-64" data-id="element-586">
                <div className="flex items-center justify-between mb-4" data-id="element-587">
                  <button onClick={() => {
                const d = new Date(currentDate);
                d.setMonth(d.getMonth() - 1);
                setCurrentDate(d);
              }} className="p-1 text-text-secondary hover:text-text-primary hover:bg-clinical-gray-50 rounded" data-id="element-588">
                    <ChevronLeftIcon size={16} data-id="element-589" />
                  </button>
                  <span className="text-sm font-medium text-text-primary" data-id="element-590">
                    {MONTHS_IT[currentDate.getMonth()]}{' '}
                    {currentDate.getFullYear()}
                  </span>
                  <button onClick={() => {
                const d = new Date(currentDate);
                d.setMonth(d.getMonth() + 1);
                setCurrentDate(d);
              }} className="p-1 text-text-secondary hover:text-text-primary hover:bg-clinical-gray-50 rounded" data-id="element-591">
                    <ChevronRightIcon size={16} data-id="element-592" />
                  </button>
                </div>
                <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-593">
                  {DAYS_SHORT_IT.map((d, i) => {
                const displayDay = i === 6 ? DAYS_SHORT_IT[0] : DAYS_SHORT_IT[i + 1];
                return <div key={i} className="text-[10px] font-medium text-text-tertiary" data-id="element-594">
                        {displayDay.charAt(0)}
                      </div>;
              })}
                </div>
                <div className="grid grid-cols-7 gap-1 text-center" data-id="element-595">
                  {Array.from({
                length: new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay() === 0 ? 6 : new Date(currentDate.getFullYear(), currentDate.getMonth(), 1).getDay() - 1
              }).map((_, i) => <div key={`empty-${i}`} className="w-6 h-6" data-id="element-596" />)}
                  {Array.from({
                length: new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0).getDate()
              }).map((_, i) => {
                const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i + 1);
                const isSelected = isSameDay(date, currentDate);
                return <button key={i} onClick={() => {
                  setCurrentDate(date);
                  setShowDatePicker(false);
                }} className={`w-6 h-6 mx-auto flex items-center justify-center rounded-full text-xs transition-colors ${isSelected ? 'bg-medical-green text-white font-medium' : 'text-text-primary hover:bg-clinical-gray-100'}`} data-id="element-597">
                        {i + 1}
                      </button>;
              })}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {!isMobile && !isTablet && <div className="flex bg-clinical-gray-100 p-1 rounded-medical border border-clinical-gray-200" data-id="element-598">
            {(['day', 'week', 'month', 'year'] as ViewMode[]).map(v => <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 text-sm font-medium rounded transition-all ${view === v ? 'bg-white text-medical-green shadow-sm' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-599">
                {v === 'day' ? 'Giorno' : v === 'week' ? 'Settimana' : v === 'month' ? 'Mese' : 'Anno'}
              </button>)}
          </div>}
      </div>

      {/* Filters */}
      {isMobile ? <div className="shrink-0" data-id="element-600">
          <button onClick={() => setShowMobileFilters(!showMobileFilters)} className="w-full flex items-center justify-center gap-2 medical-btn-secondary py-2" data-id="element-601">
            <FilterIcon size={16} data-id="element-602" />
            {showMobileFilters ? 'Nascondi filtri' : 'Mostra filtri'}
            {(selectedDoctors.length > 0 || selectedExams.length > 0 || selectedMachines.length > 0) && <span className="bg-medical-green text-white text-xs px-1.5 py-0.5 rounded-full ml-1" data-id="element-603">
                {selectedDoctors.length + selectedExams.length + selectedMachines.length}
              </span>}
          </button>

          <AnimatePresence data-id="element-604">
            {showMobileFilters && <motion.div initial={{
          height: 0,
          opacity: 0
        }} animate={{
          height: 'auto',
          opacity: 1
        }} exit={{
          height: 0,
          opacity: 0
        }} className="overflow-hidden mt-3 space-y-3" data-id="element-605">
                {role !== 'medico' && <MultiSelectFilter label="Medici" options={DOCTORS} selected={selectedDoctors} onChange={setSelectedDoctors} isOpen={openFilter === 'doctors'} onToggle={() => setOpenFilter(openFilter === 'doctors' ? null : 'doctors')} onClose={() => setOpenFilter(null)} data-id="element-606" />}
                <MultiSelectFilter label="Tipo Esame" options={EXAM_TYPES} selected={selectedExams} onChange={setSelectedExams} isOpen={openFilter === 'exams'} onToggle={() => setOpenFilter(openFilter === 'exams' ? null : 'exams')} onClose={() => setOpenFilter(null)} data-id="element-607" />
                <MultiSelectFilter label="Macchinari" options={MACHINES} selected={selectedMachines} onChange={setSelectedMachines} isOpen={openFilter === 'machines'} onToggle={() => setOpenFilter(openFilter === 'machines' ? null : 'machines')} onClose={() => setOpenFilter(null)} data-id="element-608" />
              </motion.div>}
          </AnimatePresence>
        </div> : <div className="flex flex-wrap items-center gap-3 shrink-0 bg-white p-3 rounded-medical-lg border border-clinical-gray-100 shadow-sm" data-id="element-609">
          <FilterIcon size={16} className="text-text-tertiary ml-1" data-id="element-610" />
          {role !== 'medico' && <MultiSelectFilter label="Medici" options={DOCTORS} selected={selectedDoctors} onChange={setSelectedDoctors} isOpen={openFilter === 'doctors'} onToggle={() => setOpenFilter(openFilter === 'doctors' ? null : 'doctors')} onClose={() => setOpenFilter(null)} data-id="element-611" />}
          <MultiSelectFilter label="Tipo Esame" options={EXAM_TYPES} selected={selectedExams} onChange={setSelectedExams} isOpen={openFilter === 'exams'} onToggle={() => setOpenFilter(openFilter === 'exams' ? null : 'exams')} onClose={() => setOpenFilter(null)} data-id="element-612" />
          <MultiSelectFilter label="Macchinari" options={MACHINES} selected={selectedMachines} onChange={setSelectedMachines} isOpen={openFilter === 'machines'} onToggle={() => setOpenFilter(openFilter === 'machines' ? null : 'machines')} onClose={() => setOpenFilter(null)} data-id="element-613" />

          {(selectedDoctors.length > 0 || selectedExams.length > 0 || selectedMachines.length > 0) && <button onClick={() => {
        setSelectedDoctors([]);
        setSelectedExams([]);
        setSelectedMachines([]);
      }} className="text-xs text-text-tertiary hover:text-status-error flex items-center gap-1 ml-auto transition-colors" data-id="element-614">
              <XIcon size={14} data-id="element-615" /> Resetta filtri
            </button>}
        </div>}

      {/* Main View Area */}
      {isMobile ? renderMobileAgenda() : <AnimatePresence mode="wait" data-id="element-616">
          <motion.div key={view} initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.2
      }} className="flex-1 min-h-0 flex flex-col" data-id="element-617">
            {view === 'day' && renderDayView()}
            {view === 'week' && renderWeekView()}
            {view === 'month' && renderMonthView()}
            {view === 'year' && renderYearView()}
          </motion.div>
        </AnimatePresence>}

      {/* Event Detail Modal */}
      <ModalDialog isOpen={!!selectedEvent} onClose={() => setSelectedEvent(null)} title="Dettaglio Evento" maxWidth="max-w-md" footer={<button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setSelectedEvent(null)} data-id="element-619">
            Chiudi
          </button>} data-id="element-618">
        {selectedEvent && <div className="space-y-4" data-id="element-620">
            <div className="grid grid-cols-2 gap-4" data-id="element-621">
              <div data-id="element-622">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-623">
                  Paziente
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-624">
                  {selectedEvent.patientName}
                </span>
              </div>
              <div data-id="element-625">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-626">
                  Tipologia esame
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-627">
                  {selectedEvent.examType}
                </span>
              </div>
              <div data-id="element-628">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-629">
                  Orario
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-630">
                  {formatTime(selectedEvent.startHour)} -{' '}
                  {formatTime(selectedEvent.startHour + selectedEvent.duration)}
                </span>
              </div>
              <div data-id="element-631">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-632">
                  Medico
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-633">
                  {selectedEvent.doctor}
                </span>
              </div>
              <div data-id="element-634">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-635">
                  Macchinario
                </span>
                <span className="text-sm font-medium text-text-primary" data-id="element-636">
                  {selectedEvent.machine}
                </span>
              </div>
              <div data-id="element-637">
                <span className="text-xs text-text-tertiary block mb-1" data-id="element-638">
                  Status esame
                </span>
                <StatusBadge status={selectedEvent.date < new Date() ? 'Refertato' : 'Programmato'} data-id="element-639" />
              </div>
            </div>
          </div>}
      </ModalDialog>
    </div>;
}